import React, { useEffect, useState } from "react";
import api from "../../pages/chat/api";
import { toast } from "../../utils/appToast";
import Tooltip from "./Projects/Project Page/Tooltip";

const CreateTagModals = ({
  isOpen,
  onClose,
  documentId,
  onTagUpdate,
  onClearSelection,
  displayedFiles = [], // Array passed from KnowledgeBase.js
}) => {
  const [allTags, setAllTags] = useState([]);
  
  // Selection Tracking using Set for immediate toggle
  const [selectedTagIds, setSelectedTagIds] = useState(new Set());
  
  const [searchTerm, setSearchTerm] = useState("");
  const [showTagLimitWarning, setShowTagLimitWarning] = useState(false);

  // UI Modes
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [isRenameMode, setIsRenameMode] = useState(false);
  
  // Action Tracking
  const [tagsToDelete, setTagsToDelete] = useState([]);
  const [tagsToRename, setTagsToRename] = useState({});

  const [needsParentUpdate, setNeedsParentUpdate] = useState(false);
  const [needsSelectionClear, setNeedsSelectionClear] = useState(false);

  // Form States (Create)
  const [fieldName, setFieldName] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [nameError, setNameError] = useState("");
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("Save");

  const colorOptions = [
    "#198808", "#0369A1", "#8B55DD", "#E74746", "#BF8C00",
  ];

  const fetchTags = async (silent = false) => {
    const docIds = Array.isArray(documentId) ? documentId : [documentId];
    if (!docIds.length) return;

    if (!silent) setIsLoading(true);
    
    try {
      // 1. Fetch EVERY tag available in the system
      const allTagsRes = await api.get("/api/tagging/tags/");
      setAllTags(allTagsRes.data || []);
      
      // 2. Extract applied tags instantly from KnowledgeBase displayedFiles
      const appliedTagIds = new Set();
      
      docIds.forEach((id) => {
        const doc = displayedFiles.find(f => f.id === id);
        
        if (doc && Array.isArray(doc.tags)) {
          doc.tags.forEach((tag) => {
            if (tag && typeof tag.id === "number") {
              appliedTagIds.add(tag.id);
            }
          });
        }
      });

      setSelectedTagIds(appliedTagIds);
    } catch (err) {
      toast.error("Failed to load tags.", { position: "top-right", autoClose: 4000 });
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const docIdKey = Array.isArray(documentId) ? [...documentId].sort().join(',') : documentId;

  useEffect(() => {
    if (isOpen && documentId) {
      setNeedsParentUpdate(false);
      setNeedsSelectionClear(false);
      
      setAllTags([]);
      setSelectedTagIds(new Set());
      setSearchTerm("");
      setShowTagLimitWarning(false);
      setIsCreating(false);
      setIsDeleteMode(false);
      setIsRenameMode(false);
      setTagsToDelete([]);
      setTagsToRename({});
      setFieldName("");
      setSelectedColor("");
      setNameError("");
      setSaveStatus("Save");
      fetchTags(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, docIdKey]);

  const handleModalClose = () => {
    if (needsParentUpdate) {
      onTagUpdate?.();
    }
    if (needsSelectionClear) {
      onClearSelection?.();
    }
    onClose();
  };

  // Check if a real change was made (used to disable/enable the Save button)
  const hasRenameOrDeleteChanges = () => {
    const hasDeletions = tagsToDelete.length > 0;
    const hasActualRenames = Object.entries(tagsToRename).some(([id, newName]) => {
      const originalTag = allTags.find((t) => t.id === Number(id));
      // Only count as a change if the name is actually different from the original and not empty
      return originalTag && newName.trim() !== originalTag.name && newName.trim() !== "";
    });
    return hasDeletions || hasActualRenames;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    const isValid = /^[a-zA-Z0-9\s]*$/.test(value);
    if (isValid) {
      setFieldName(value);
      setNameError("");
    } else {
      setNameError("Only letters, numbers, and spaces are allowed");
    }
  };

  const handleRenameChange = (tagId, newName) => {
    if (/^[a-zA-Z0-9\s]*$/.test(newName)) {
      setTagsToRename(prev => ({
        ...prev,
        [tagId]: newName
      }));
    }
  };

  const toggleTagAssignment = async (tagId, isAdding) => {
    const docIds = Array.isArray(documentId) ? documentId : [documentId];
    
    // Optimistic UI Update instantly
    setSelectedTagIds(prev => {
      const newSet = new Set(prev);
      if (isAdding) newSet.add(tagId);
      else newSet.delete(tagId);
      return newSet;
    });

    try {
      const updatedArray = isAdding 
        ? [...Array.from(selectedTagIds), tagId]
        : Array.from(selectedTagIds).filter(id => id !== tagId);

      // Auto-apply tag directly to backend
      await Promise.all(
        docIds.map((id) =>
          api.post(`/api/v1/rfp/document-tags/${id}/add_tags/`, {
            tag_ids: updatedArray,
          })
        )
      );
      setNeedsParentUpdate(true);
      setNeedsSelectionClear(true);
    } catch (err) {
      toast.error("Failed to apply tag update");
      // Revert optimistic update
      setSelectedTagIds(prev => {
        const newSet = new Set(prev);
        if (isAdding) newSet.delete(tagId);
        else newSet.add(tagId);
        return newSet;
      });
    }
  };

  const handleAddField = async () => {
    if (!/^[a-zA-Z0-9\s]+$/.test(fieldName.trim())) {
      setNameError("Name can only contain letters, numbers, and spaces");
      return;
    }

    if (!fieldName.trim() || !selectedColor) {
      if (!fieldName.trim()) setNameError("Name is required");
      return;
    }
    
    setSaveStatus("Saving...");
    setIsSubmitting(true);

    try {
      const res = await api.post("/api/tagging/tags/", {
        name: fieldName.trim(),
        color: selectedColor,
        description: "",
      });

      const newTag = res.data;
      setAllTags((prev) => [...prev, newTag]);

      if (selectedTagIds.size >= 3) {
        setShowTagLimitWarning(true);
        setTimeout(() => setShowTagLimitWarning(false), 3000);
      } else {
        // Instantly apply the newly created tag to the document
        await toggleTagAssignment(newTag.id, true);
      }

      setSaveStatus("Saved");
      setNeedsParentUpdate(true);

      setTimeout(() => {
        setFieldName("");
        setSelectedColor("");
        setNameError("");
        setIsCreating(false);
        setSearchTerm("");
        setSaveStatus("Save");
        setIsSubmitting(false);
      }, 2000);
      
    } catch (err) {
      setIsSubmitting(false);
      setSaveStatus("Save");
      const msg = err?.response?.data?.detail || "Failed to create tag.";
      toast.error(msg);
    }
  };
  const handleSaveRenamesAndDeletes = async () => {
    setIsSubmitting(true);
    setSaveStatus("Saving...");

    try {
      // 1. Process Renames
      const renamePromises = Object.entries(tagsToRename).map(([id, newName]) => {
        const originalTag = allTags.find(t => t.id === Number(id));
        if (originalTag && newName.trim() && newName.trim() !== originalTag.name) {
          return api.put(`/api/tagging/tags/${id}/`, {
            name: newName.trim(),
            color: originalTag.color
          });
        }
        return Promise.resolve();
      });
      await Promise.all(renamePromises);

      // 2. Process Deletes (Using your provided logic)
      if (tagsToDelete.length > 0) {
        const deletedIds = []; // Track successfully deleted IDs
        
        for (const id of tagsToDelete) {
          try {
            await api.delete(`/api/tagging/tags/${id}/`);
            deletedIds.push(id);
          } catch (innerErr) {
            console.error(`Failed to delete tag ${id}`, innerErr);
            // I swapped "showPopup" to "toast.error" to match the rest of your component
            toast.error(`Failed to delete tag with ID ${id}`);
          }
        }

        // 3. Instantly remove successfully deleted tags from the local UI state
        if (deletedIds.length > 0) {
          setAllTags((prevTags) => 
            prevTags.filter((tag) => !deletedIds.includes(tag.id))
          );
          setNeedsParentUpdate(true);
        }
      }

      setSaveStatus("Saved");
      
      // Since the UI updates instantly now, we can reduce the timeout to 1 second
      setTimeout(() => {
        setIsRenameMode(false);
        setIsDeleteMode(false);
        setTagsToDelete([]);
        setTagsToRename({});
        setSaveStatus("Save");
        setIsSubmitting(false);
        // Fetch tags in the background just to sync with the server
        fetchTags(true); 
      }, 1000);

    } catch (err) {
      setIsSubmitting(false);
      setSaveStatus("Save");
      toast.error("Failed to save changes");
    }
  };

  const handleCancelChanges = () => {
    setTagsToDelete([]);
    setTagsToRename({});
    setIsDeleteMode(false);
    setIsRenameMode(false);
  };

  const toggleDeleteMode = () => {
    setIsDeleteMode(!isDeleteMode);
    setIsRenameMode(false);
    if (isDeleteMode) setTagsToDelete([]);
  };

  const toggleRenameMode = () => {
    setIsRenameMode(!isRenameMode);
    setIsDeleteMode(false);
    if (isRenameMode) setTagsToRename({});
  };

  function darkenColor(hex, amount = 0.2) {
    if (!hex || !hex.startsWith("#")) return "#333";
    const num = parseInt(hex.slice(1), 16);
    let r = (num >> 16) & 255; let g = (num >> 8) & 255; let b = num & 255;
    r = Math.floor(r * (1 - amount));
    g = Math.floor(g * (1 - amount));
    b = Math.floor(b * (1 - amount));
    return `rgb(${r}, ${g}, ${b})`;
  }

  const renderTagPill = (tag) => {
    if (!tag?.id || !tag?.name) return null;
    
    const isSelected = selectedTagIds.has(tag.id);
    const showCross = isSelected || isDeleteMode;
    const currentName = tagsToRename[tag.id] !== undefined ? tagsToRename[tag.id] : tag.name;

    const handleClick = () => {
      if (isRenameMode) return; 

      if (isDeleteMode) {
        // Toggle deletion staging
        if (tagsToDelete.includes(tag.id)) {
          setTagsToDelete((prev) => prev.filter(id => id !== tag.id));
        } else {
          setTagsToDelete((prev) => [...prev, tag.id]);
        }
      } else {
        // Instantly Auto-Apply or Auto-Remove tags when clicked
        if (isSelected) {
          setShowTagLimitWarning(false);
          toggleTagAssignment(tag.id, false);
        } else {
          if (selectedTagIds.size >= 3) {
            setShowTagLimitWarning(true);
            setTimeout(() => setShowTagLimitWarning(false), 3000);
            return;
          }
          setShowTagLimitWarning(false);
          toggleTagAssignment(tag.id, true);
        }
      }
    };

    const inputWidth = `${Math.max(currentName.length, 1) * 8 + 10}px`;

    return (
      <div
        key={tag.id}
        onClick={handleClick}
        // Prevents wrapping and sets max container size
        className={`flex items-center gap-1 flex-shrink-0 max-w-[150px] ${!isRenameMode ? "cursor-pointer px-3 py-[4px] rounded-full" : "px-0 py-[2px] mx-2"} text-[13px] font-medium transition-all select-none ${isDeleteMode ? "hover:opacity-75" : ""} ${tagsToDelete.includes(tag.id) ? "opacity-40 line-through" : ""}`}
        style={{
          color: darkenColor(tag.color),
          backgroundColor: isRenameMode ? "transparent" : (isSelected ? `${tag.color}1A` : "transparent"),
          border: isRenameMode ? "none" : `1px solid ${isDeleteMode && tagsToDelete.includes(tag.id) ? "#ef4444" : tag.color || "#CCCCCC"}`, 
          borderBottom: isRenameMode ? `1.5px solid ${tag.color}` : `1px solid ${isDeleteMode && tagsToDelete.includes(tag.id) ? "#ef4444" : tag.color || "#CCCCCC"}`,
        }}
      >
        {isRenameMode ? (
          <input
            type="text"
            value={currentName}
            onChange={(e) => handleRenameChange(tag.id, e.target.value)}
            className="bg-transparent border-none outline-none p-0 m-0 focus:ring-0 text-[13px] font-medium min-w-[30px] max-w-[120px] truncate"
            style={{ color: darkenColor(tag.color), width: inputWidth, fontSize: '14px', lineHeight: '20px' }}
          />
        ) : (
          // truncate class makes long text wrap perfectly into "..."
          <span className="truncate block max-w-[120px]">{currentName}</span>
        )}

        {showCross && !isRenameMode && (
          <span className="ml-[2px] text-[16px] font-light leading-none flex items-center flex-shrink-0">
            &times;
          </span>
        )}
      </div>
    );
  };

  const sortedAndFilteredTags = [...allTags]
    .sort((a, b) => b.id - a.id)
    .filter((tag) => tag.name.toLowerCase().includes(searchTerm.toLowerCase()));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center font-[Outfit]">
      <div className="relative w-[520px] bg-white rounded-[16px] shadow-2xl pb-5 pt-3 px-5">
        <button
          className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition"
          onClick={handleModalClose}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="mb-1">
          <h2 className="text-[18px] font-semibold text-[#1A1A1A]">Apply tags</h2>
          <p className="text-[13px] text-[#4D4D4D]">
            Select from existing tags or create a new one
          </p>
        </div>

        <hr className="border-[#D1D1D1] mb-4" />

        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search Tags..."
              className="w-full pl-9 pr-3 py-[8px] text-[14px] border border-[#00000036] rounded-[8px] focus:outline-none focus:border-[#9b59b6] placeholder-gray-400 disabled:bg-gray-50"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isDeleteMode || isRenameMode}
              style={{ fontSize: '14px', lineHeight: '20px' }}
            />
          </div>

          {!isCreating ? (
            <button
              onClick={() => setIsCreating(true)}
              disabled={isDeleteMode || isRenameMode}
              className={`px-4 py-[8px] text-white rounded-[8px] text-[14px] font-medium flex items-center gap-2 transition ${isDeleteMode || isRenameMode ? "bg-gray-300" : "bg-[#BB42AE] hover:bg-[#A04A97]"}`}
            >
              <span className="text-[18px] font-light leading-none">+</span> Create Tag
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setIsCreating(false)}
                className="px-4 py-[8px] text-[#BB42AE] border border-[#BB42AE] rounded-[8px] text-[14px] font-medium hover:bg-purple-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddField}
                disabled={!fieldName.trim() || !selectedColor || nameError || isSubmitting}
                className="px-4 py-[8px] bg-[#BB42AE] hover:bg-[#a53699] text-white rounded-[8px] text-[14px] font-medium disabled:opacity-50 transition w-[85px] flex justify-center"
              >
                {saveStatus}
              </button>
            </div>
          )}
        </div>

        {showTagLimitWarning && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-[13px] font-medium">
              Maximum 3 tags allowed. Remove a tag to add another.
            </p>
          </div>
        )}

        {!isCreating ? (
          <div>
            <div className="flex justify-between items-center mb-2 min-h-[24px]">
              <span className="text-[#4D4D4D] text-[15px] font-medium">
                {isDeleteMode ? <span className="">Select tags to delete</span> : 
                 isRenameMode ? <span className="">Rename tags</span> : "Tags"}
              </span>
              
              <div className="flex items-center ">
                {/* 1. Only show Edit/Delete buttons when we are NOT in either mode */}
                {!isRenameMode && !isDeleteMode ? (
                  <div className="flex gap-3 text-gray-500">
                    <Tooltip content="Rename Tags" placement="top">
                      <button 
                        onClick={toggleRenameMode} 
                        className="transition hover:text-blue-500" 
                      >
                       <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M1.0125 13.5C0.736425 13.5 0.498656 13.4065 0.299194 13.2194C0.0997313 13.0323 0 12.8059 0 12.5401C0 12.2784 0.0997313 12.053 0.299194 11.8639C0.498656 11.6748 0.736425 11.5802 1.0125 11.5802H12.4875C12.7636 11.5802 13.0013 11.6737 13.2008 11.8608C13.4003 12.0479 13.5 12.2743 13.5 12.5401C13.5 12.8018 13.4003 13.0273 13.2008 13.2164C13.0013 13.4055 12.7636 13.5 12.4875 13.5H1.0125ZM2.7 8.64516H3.53464L9.13714 3.34348L8.71273 2.93505L8.29221 2.54245L2.7 7.85388V8.64516ZM1.6875 9.02656V7.68654C1.6875 7.60943 1.70111 7.536 1.72834 7.46624C1.75567 7.39649 1.8009 7.33165 1.86401 7.2717L9.25408 0.281731C9.35184 0.189047 9.46282 0.118921 9.58703 0.0713527C9.71111 0.0237845 9.83936 0 9.97178 0C10.1086 0 10.2386 0.0237845 10.3619 0.0713527C10.4852 0.118921 10.5992 0.192353 10.704 0.29165L11.5152 1.07061C11.6199 1.16329 11.6956 1.26909 11.7423 1.38801C11.7891 1.50704 11.8125 1.63135 11.8125 1.76093C11.8125 1.87996 11.7891 1.99829 11.7423 2.11593C11.6956 2.23368 11.6199 2.3422 11.5152 2.4415L4.14214 9.43147C4.07891 9.49142 4.01057 9.5353 3.93711 9.56314C3.86353 9.59108 3.78608 9.60506 3.70474 9.60506H2.2977C2.12366 9.60506 1.97848 9.54986 1.86216 9.43947C1.74572 9.32919 1.6875 9.19155 1.6875 9.02656ZM9.13714 3.34348L8.71273 2.93505L8.29221 2.54245L9.13714 3.34348Z" fill="black" fillOpacity="0.7"/>
                        </svg>
                      </button>
                    </Tooltip>
                    <Tooltip content="Delete Tags" placement="top">
                      <button 
                        onClick={toggleDeleteMode} 
                        className="transition hover:text-red-500" 
                      >
                       <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                          <path d="M2.10581 12.6634C1.73294 12.6634 1.41375 12.5306 1.14825 12.2651C0.88275 11.9996 0.75 11.6804 0.75 11.3076V1.78838H0.5625C0.403125 1.78838 0.269563 1.73444 0.161813 1.62656C0.0539376 1.51869 0 1.38506 0 1.22569C0 1.06619 0.0539376 0.932625 0.161813 0.825C0.269563 0.71725 0.403125 0.663375 0.5625 0.663375H3.375C3.375 0.47975 3.43969 0.323251 3.56906 0.193875C3.69831 0.0646255 3.85475 0 4.03837 0H7.21163C7.39525 0 7.55169 0.0646255 7.68094 0.193875C7.81031 0.323251 7.875 0.47975 7.875 0.663375H10.6875C10.8469 0.663375 10.9804 0.717313 11.0882 0.825188C11.1961 0.933063 11.25 1.06669 11.25 1.22606C11.25 1.38556 11.1961 1.51913 11.0882 1.62675C10.9804 1.7345 10.8469 1.78838 10.6875 1.78838H10.5V11.3076C10.5 11.6804 10.3673 11.9996 10.1018 12.2651C9.83625 12.5306 9.51706 12.6634 9.14419 12.6634H2.10581ZM9.375 1.78838H1.875V11.3076C1.875 11.3749 1.89662 11.4302 1.93987 11.4735C1.98312 11.5167 2.03844 11.5384 2.10581 11.5384H9.14419C9.21156 11.5384 9.26688 11.5167 9.31013 11.4735C9.35337 11.4302 9.375 11.3749 9.375 11.3076V1.78838ZM4.64138 9.87675C4.749 9.76888 4.80281 9.63525 4.80281 9.47588V3.85087C4.80281 3.6915 4.74887 3.55787 4.641 3.45C4.53325 3.34225 4.39963 3.28837 4.24013 3.28837C4.08075 3.28837 3.94719 3.34225 3.83944 3.45C3.73181 3.55787 3.678 3.6915 3.678 3.85087V9.47588C3.678 9.63525 3.73188 9.76888 3.83963 9.87675C3.9475 9.9845 4.08119 10.0384 4.24069 10.0384C4.40006 10.0384 4.53363 9.9845 4.64138 9.87675ZM7.41056 9.87675C7.51819 9.76888 7.572 9.63525 7.572 9.47588V3.85087C7.572 3.6915 7.51813 3.55787 7.41037 3.45C7.3025 3.34225 7.16881 3.28837 7.00931 3.28837C6.84994 3.28837 6.71638 3.34225 6.60863 3.45C6.501 3.55787 6.44719 3.6915 6.44719 3.85087V9.47588C6.44719 9.63525 6.50112 9.76888 6.609 9.87675C6.71675 9.9845 6.85037 10.0384 7.00987 10.0384C7.16925 10.0384 7.30281 9.9845 7.41056 9.87675Z" fill="black" fillOpacity="0.7"/>
                        </svg>
                      </button>
                    </Tooltip>
                  </div>
                ) : (
                  /* 2. Show Cancel/Save when in Edit/Delete Mode */
                  <div className="flex  text-[14px] font-medium">
                    <button 
                      onClick={handleCancelChanges} 
                      className="text-[#808080] hover:text-gray-700 transition px-1 py-1"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    {/* 3. Disable Save until a real change is made */}
                    <button
                      onClick={handleSaveRenamesAndDeletes}
                      disabled={isSubmitting || !hasRenameOrDeleteChanges()}
                      className={`transition min-w-[50px] text-center  px-1 py-1 ${
                        !hasRenameOrDeleteChanges() 
                          ? " text-[#808080] cursor-not-allowed" 
                          : "text-[#BB42AE] "
                      }`}
                    >
                      {saveStatus}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {isLoading ? (
              <div className="text-[13px] text-gray-500">Loading tags...</div>
            ) : (
              <div className="flex overflow-x-auto flex-nowrap w-full gap-3 pb-2 snap-x custom-scrollbar items-center">
                {sortedAndFilteredTags.length > 0 ? (
                  sortedAndFilteredTags.map((tag) => renderTagPill(tag))
                ) : (
                  <span className="text-gray-400 text-[13px]">
                    {isDeleteMode ? "All tags staged for deletion." : "No tags found."}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="animate-in fade-in slide-in-from-top-2 duration-200 space-y-2 mt-[-4px]">
            <div>
              <label className="block text-[14px] font-medium text-gray-800 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Eg. Contracts"
                value={fieldName}
                onChange={handleNameChange}
                className={`w-full px-3 py-[8px] text-[14px] border rounded-[8px] focus:outline-none focus:border-[#9b59b6] placeholder-gray-400 ${
                  nameError ? "border-red-400" : "border-gray-200"
                }`}
                style={{ fontSize: '14px', lineHeight: '20px' }}
              />
              {nameError && (
                <p className="text-red-500 text-[12px] mt-1">{nameError}</p>
              )}
            </div>

            <div>
              <label className="block text-[14px] font-medium text-gray-800 mb-2">
                Color <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                {colorOptions.map((color, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedColor(color)}
                    className="w-7 h-7 rounded-[6px] transition-all duration-200 hover:scale-110"
                    style={{
                      backgroundColor: color,
                      outline:
                        selectedColor === color
                          ? "2px solid #52525B"
                          : "none",
                      outlineOffset: "2px",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateTagModals;
